import React, { useMemo, useState, useRef, memo } from 'react';
import { Stage, Layer, Rect, Line, Text, Group } from 'react-konva';
import chroma from 'chroma-js';
import PropTypes from 'prop-types';
import './PlayoffScoreGrid.css';

const CELL_SIZE = 15; // pixels per cell
const LABEL_OFFSET = 30; // Space for axis labels
const AXIS_LABEL_SPACE = 25; // Additional space for axis labels below/left

// User color palette for predictions
const USER_COLORS = [
  '#00ff88', // Green
  '#ff6b6b', // Red
  '#4dabf7', // Blue
  '#ffd43b', // Yellow
  '#ff6b9d', // Pink
  '#51cf66', // Light Green
  '#ff922b', // Orange
  '#845ef7', // Purple
];

// Default empty data for safety (should not be used in production)
const DEFAULT_PREDICTIONS = [];
const DEFAULT_GAME_DATA = {
  spread: null,
  overUnder: null,
  homeTeamName: 'Home Team',
  awayTeamName: 'Away Team',
  currentScore: null,
  scoreHistory: null,
};

const PlayoffScoreGrid = ({ predictions = DEFAULT_PREDICTIONS, gameData = DEFAULT_GAME_DATA, height = 500 }) => {
  const [hoveredCell, setHoveredCell] = useState(null);
  const [tooltip, setTooltip] = useState(null);
  const [hoveredRegion, setHoveredRegion] = useState(null); // Track region for stable tooltip
  const stageRef = useRef(null);

  // Calculate dynamic grid size based on predictions and current score
  const gridDimensions = useMemo(() => {
    const predictionScores = {
      maxHome: 0,
      maxAway: 0
    };

    // Include predictions in grid size calculation
    if (predictions && predictions.length > 0) {
      predictionScores.maxHome = Math.max(...predictions.map(p => p.home || 0));
      predictionScores.maxAway = Math.max(...predictions.map(p => p.away || 0));
    }

    // Include current score in grid size calculation
    if (gameData?.currentScore) {
      predictionScores.maxHome = Math.max(predictionScores.maxHome, gameData.currentScore.home || 0);
      predictionScores.maxAway = Math.max(predictionScores.maxAway, gameData.currentScore.away || 0);
    }

    // Include score history in grid size calculation
    if (gameData?.scoreHistory && gameData.scoreHistory.length > 0) {
      gameData.scoreHistory.forEach(score => {
        predictionScores.maxHome = Math.max(predictionScores.maxHome, score.home || 0);
        predictionScores.maxAway = Math.max(predictionScores.maxAway, score.away || 0);
      });
    }

    // Add 7 to each dimension for visual padding, minimum 30x30
    return {
      maxHome: Math.max(predictionScores.maxHome + 7, 30),
      maxAway: Math.max(predictionScores.maxAway + 7, 30)
    };
  }, [predictions, gameData?.currentScore, gameData?.scoreHistory]);

  const GRID_SIZE_HOME = gridDimensions.maxHome;
  const GRID_SIZE_AWAY = gridDimensions.maxAway;
  const STAGE_WIDTH = GRID_SIZE_AWAY * CELL_SIZE + LABEL_OFFSET + AXIS_LABEL_SPACE;
  const STAGE_HEIGHT = GRID_SIZE_HOME * CELL_SIZE + LABEL_OFFSET + AXIS_LABEL_SPACE;

  // Calculate which user wins for each score combination
  const cellShading = useMemo(() => {
    const shading = {};
    
    // Early return if no predictions
    if (!predictions || predictions.length === 0) {
      return shading;
    }
    
    // Use the calculated grid dimensions
    const maxHome = GRID_SIZE_HOME;
    const maxAway = GRID_SIZE_AWAY;
    
    for (let home = 0; home <= maxHome; home++) {
      for (let away = 0; away <= maxAway; away++) {
        // Calculate absolute score difference for each prediction
        const diffs = predictions
          .filter(pred => pred != null && typeof pred.home === 'number' && typeof pred.away === 'number')
          .map((pred, index) => {
            const homeDiff = Math.abs(home - (pred.home || 0));
            const awayDiff = Math.abs(away - (pred.away || 0));
            const totalDiff = homeDiff + awayDiff;
            return { index, diff: totalDiff, user: pred };
          });

        // Skip if no valid predictions
        if (diffs.length === 0) continue;

        // Find minimum difference
        diffs.sort((a, b) => a.diff - b.diff);
        const minDiff = diffs[0].diff;
        
        // Find all users with minimum difference (ties)
        const winners = diffs.filter(d => d.diff === minDiff);
        
        if (winners.length === 1) {
          // Single winner
          const winner = winners[0].user;
          shading[`${home}-${away}`] = {
            users: [winner],
            isTie: false,
            color: winner.color || USER_COLORS[0],
            diff: minDiff,
          };
        } else {
          // Tie
          shading[`${home}-${away}`] = {
            users: winners.map(w => w.user),
            isTie: true,
            color: '#ffd700', // Gold for ties
            diff: minDiff,
          };
        }
      }
    }
    
    return shading;
  }, [predictions, GRID_SIZE_HOME, GRID_SIZE_AWAY]);

  // Calculate region key for stable hover (same color/type = same region)
  const getRegionKey = (home, away, cellData) => {
    if (!cellData) return null;
    // Create a key based on the winner(s) and tie status
    const userKeys = cellData.users.map(u => u.username || u).sort().join(',');
    return `${cellData.isTie ? 'tie' : 'single'}-${userKeys}`;
  };

  // Render grid cells with improved hover detection
  const renderGridCells = () => {
    const cells = [];
    
    for (let home = 0; home <= GRID_SIZE_HOME; home++) {
      for (let away = 0; away <= GRID_SIZE_AWAY; away++) {
        const cellData = cellShading[`${home}-${away}`];
        if (!cellData) continue;
        
        const x = away * CELL_SIZE;
        const y = home * CELL_SIZE;
        const regionKey = getRegionKey(home, away, cellData);
        const isInHoveredRegion = hoveredRegion === regionKey;
        const isHovered = hoveredCell?.home === home && hoveredCell?.away === away;
        
        // Check if this is a user's prediction cell
        const isPredictionCell = predictions.some(
          p => p && typeof p.home === 'number' && typeof p.away === 'number' && p.home === home && p.away === away
        );
        
        // Lighten color on hover
        const cellColor = (isHovered || isInHoveredRegion)
          ? chroma(cellData.color).brighten(0.3).hex()
          : chroma(cellData.color).alpha(0.6).hex();
        
        cells.push(
          <Rect
            key={`cell-${home}-${away}`}
            x={x}
            y={y}
            width={CELL_SIZE}
            height={CELL_SIZE}
            fill={cellColor}
            stroke={cellData.isTie ? '#ffd700' : '#333'}
            strokeWidth={isHovered ? 2 : 0.5}
            onMouseEnter={() => {
              // Only update if we're entering a new region or new cell
              if (hoveredRegion !== regionKey || !hoveredCell || hoveredCell.home !== home || hoveredCell.away !== away) {
                setHoveredCell({ home, away });
                setHoveredRegion(regionKey);
                
                // If this is a prediction cell, show the user's name
                if (isPredictionCell) {
                  const pred = predictions.find(p => p.home === home && p.away === away);
                  setTooltip({
                    home,
                    away,
                    ...cellData,
                    isPrediction: true,
                    predictionUser: pred,
                    x: x + CELL_SIZE / 2 + LABEL_OFFSET,
                    y: y + CELL_SIZE / 2 + LABEL_OFFSET,
                  });
                } else {
                  setTooltip({
                    home,
                    away,
                    ...cellData,
                    x: x + CELL_SIZE / 2 + LABEL_OFFSET,
                    y: y + CELL_SIZE / 2 + LABEL_OFFSET,
                  });
                }
              }
            }}
            onMouseLeave={(e) => {
              // Use requestAnimationFrame to check if we've moved to an adjacent cell
              // This prevents clearing when moving between cells of the same region
              requestAnimationFrame(() => {
                const stage = e.target.getStage();
                if (!stage) {
                  setHoveredCell(null);
                  setHoveredRegion(null);
                  setTooltip(null);
                  return;
                }
                
                const pos = stage.getPointerPosition();
                if (!pos) {
                  setHoveredCell(null);
                  setHoveredRegion(null);
                  setTooltip(null);
                  return;
                }
                
                // Check which cell we're now over (accounting for offset)
                const newAway = Math.floor((pos.x - LABEL_OFFSET) / CELL_SIZE);
                const newHome = Math.floor(pos.y / CELL_SIZE);
                
                // If we're still over a valid cell in the same region, don't clear
                if (newHome >= 0 && newHome <= GRID_SIZE_HOME && 
                    newAway >= 0 && newAway <= GRID_SIZE_AWAY) {
                  const newCellData = cellShading[`${newHome}-${newAway}`];
                  if (newCellData) {
                    const newRegionKey = getRegionKey(newHome, newAway, newCellData);
                    if (newRegionKey === regionKey) {
                      // Still in same region, don't clear
                      return;
                    }
                  }
                }
                
                // Clear if we've moved to a different region or outside
                setHoveredCell(null);
                setHoveredRegion(null);
                setTooltip(null);
              });
            }}
          />
        );
      }
    }
    
    return cells;
  };

  // Render user predictions (bold borders)
  const renderUserPredictions = () => {
    return predictions.map((pred, index) => {
      const x = pred.away * CELL_SIZE;
      const y = pred.home * CELL_SIZE;
      
      return (
        <Group key={`pred-${index}`}>
          {/* Bold border around prediction */}
          <Rect
            x={x - 1}
            y={y - 1}
            width={CELL_SIZE + 2}
            height={CELL_SIZE + 2}
            stroke={pred.color}
            strokeWidth={3}
            fill="transparent"
          />
          {/* Text label */}
          <Text
            x={x + CELL_SIZE / 2}
            y={y + CELL_SIZE / 2}
            text={pred.home + '-' + pred.away}
            fontSize={8}
            fill="white"
            align="center"
            verticalAlign="middle"
            offsetX={10}
            offsetY={4}
            fontStyle="bold"
            shadowColor="black"
            shadowBlur={2}
          />
        </Group>
      );
    });
  };

  // Render spread line (diagonal where home - away = spread)
  const renderSpreadLine = () => {
    if (!gameData || gameData.spread === null || gameData.spread === undefined || typeof gameData.spread !== 'number') {
      return null;
    }
    
    const points = [];
    const spread = Number(gameData.spread);
    
    for (let away = 0; away <= GRID_SIZE_AWAY; away++) {
      const home = away + spread;
      if (home >= 0 && home <= GRID_SIZE_HOME) {
        points.push(away * CELL_SIZE + CELL_SIZE / 2, home * CELL_SIZE + CELL_SIZE / 2);
      }
    }
    
    if (points.length < 4) return null;
    
    return (
      <Line
        points={points}
        stroke="#0066ff"
        strokeWidth={2}
        dash={[5, 5]}
        opacity={0.8}
      />
    );
  };

  // Render O/U line (diagonal where home + away = overUnder)
  const renderOverUnderLine = () => {
    if (!gameData || gameData.overUnder === null || gameData.overUnder === undefined || typeof gameData.overUnder !== 'number') {
      return null;
    }
    
    const points = [];
    const total = Number(gameData.overUnder);
    
    for (let away = 0; away <= GRID_SIZE_AWAY && away <= total; away++) {
      const home = total - away;
      if (home >= 0 && home <= GRID_SIZE_HOME) {
        points.push(away * CELL_SIZE + CELL_SIZE / 2, home * CELL_SIZE + CELL_SIZE / 2);
      }
    }
    
    if (points.length < 4) return null;
    
    return (
      <Line
        points={points}
        stroke="#ff6600"
        strokeWidth={2}
        dash={[3, 3]}
        opacity={0.8}
      />
    );
  };

  // Render game progress path with X markers
  const renderGameProgress = () => {
    // Build score history array - include scoreHistory and currentScore
    let allScores = [];
    
    // Start with score history if available
    if (gameData?.scoreHistory && Array.isArray(gameData.scoreHistory) && gameData.scoreHistory.length > 0) {
      allScores = [...gameData.scoreHistory];
    } else {
      // If no history, start with 0-0
      allScores = [{ home: 0, away: 0 }];
    }
    
    // Add current score if it's different from the last score in history
    if (gameData?.currentScore && 
        typeof gameData.currentScore.home === 'number' && 
        typeof gameData.currentScore.away === 'number') {
      const lastScore = allScores[allScores.length - 1];
      const isDifferent = !lastScore || 
        lastScore.home !== gameData.currentScore.home || 
        lastScore.away !== gameData.currentScore.away;
      
      if (isDifferent) {
        allScores.push(gameData.currentScore);
      }
    }
    
    // Filter out invalid score entries
    const validScores = allScores.filter(
      score => score && typeof score.home === 'number' && typeof score.away === 'number'
    );
    
    if (validScores.length < 1) return null;
    
    // If only one score point, still show the X marker
    const points = validScores.flatMap(score => [
      (score.away || 0) * CELL_SIZE + CELL_SIZE / 2,
      (score.home || 0) * CELL_SIZE + CELL_SIZE / 2,
    ]);
    
    // X marker size
    const X_SIZE = 6;
    
    return (
      <Group>
        {/* Path line - only show if more than one point */}
        {validScores.length > 1 && (
          <Line
            points={points}
            stroke="#ffff00"
            strokeWidth={2}
            lineCap="round"
            lineJoin="round"
            opacity={0.8}
          />
        )}
        {/* X markers at each score point */}
        {validScores.map((score, index) => {
          const x = (score.away || 0) * CELL_SIZE + CELL_SIZE / 2;
          const y = (score.home || 0) * CELL_SIZE + CELL_SIZE / 2;
          const isCurrent = index === validScores.length - 1;
          const strokeColor = isCurrent ? '#ffff00' : '#ffaa00';
          const strokeWidth = isCurrent ? 2.5 : 2;
          
          return (
            <Group key={`progress-${index}`}>
              {/* Draw X marker using two diagonal lines */}
              <Line
                points={[x - X_SIZE, y - X_SIZE, x + X_SIZE, y + X_SIZE]}
                stroke={strokeColor}
                strokeWidth={strokeWidth}
                lineCap="round"
                opacity={0.9}
              />
              <Line
                points={[x - X_SIZE, y + X_SIZE, x + X_SIZE, y - X_SIZE]}
                stroke={strokeColor}
                strokeWidth={strokeWidth}
                lineCap="round"
                opacity={0.9}
              />
            </Group>
          );
        })}
      </Group>
    );
  };

  // Render axis labels
  const renderAxisLabels = () => {
    const labels = [];
    const maxSize = Math.max(GRID_SIZE_HOME, GRID_SIZE_AWAY);
    const labelStep = Math.max(1, Math.ceil(maxSize / 15)); // Show every few scores
    
    // Y-axis (home scores) - offset to the left
    for (let i = 0; i <= GRID_SIZE_HOME; i += labelStep) {
      labels.push(
        <Text
          key={`y-label-${i}`}
          x={-10}
          y={i * CELL_SIZE + CELL_SIZE / 2}
          text={i.toString()}
          fontSize={9}
          fill="#ccc"
          align="right"
          verticalAlign="middle"
          offsetX={10}
          offsetY={4}
        />
      );
    }
    
    // X-axis (away scores) - shifted down outside the grid
    const gridBottom = GRID_SIZE_HOME * CELL_SIZE;
    for (let i = 0; i <= GRID_SIZE_AWAY; i += labelStep) {
      labels.push(
        <Text
          key={`x-label-${i}`}
          x={i * CELL_SIZE + CELL_SIZE / 2}
          y={gridBottom + 20}
          text={i.toString()}
          fontSize={9}
          fill="#ccc"
          align="center"
          verticalAlign="top"
          offsetX={5}
        />
      );
    }
    
    return labels;
  };

  // Render tooltip
  const renderTooltip = () => {
    if (!tooltip) return null;
    
    const { home, away, users, isTie, diff, x, y, isPrediction, predictionUser } = tooltip;
    
    // If hovering over a prediction cell, show just the user's name
    if (isPrediction && predictionUser) {
      return (
        <Group x={x} y={y}>
          {/* Tooltip background */}
          <Rect
            x={-40}
            y={-15}
            width={80}
            height={30}
            fill="rgba(0, 0, 0, 0.9)"
            cornerRadius={5}
            stroke={predictionUser.color || '#fff'}
            strokeWidth={2}
          />
          {/* User name */}
          <Text
            x={0}
            y={0}
            text={predictionUser.username || predictionUser.name || 'User'}
            fontSize={12}
            fill={predictionUser.color || '#fff'}
            align="center"
            verticalAlign="middle"
            offsetX={40}
            offsetY={6}
            fontStyle="bold"
          />
        </Group>
      );
    }
    
    return (
      <Group x={x} y={y}>
        {/* Tooltip background */}
        <Rect
          x={-60}
          y={-30}
          width={120}
          height={isTie ? 50 + (users.length * 15) : 50}
          fill="rgba(0, 0, 0, 0.9)"
          cornerRadius={5}
          stroke="#fff"
          strokeWidth={1}
        />
        {/* Score */}
        <Text
          x={0}
          y={-20}
          text={`${home} - ${away}`}
          fontSize={12}
          fill="#fff"
          align="center"
          verticalAlign="middle"
          offsetX={30}
          offsetY={6}
          fontStyle="bold"
        />
        {/* Winner(s) */}
        {isTie ? (
          <Text
            x={0}
            y={-5}
            text={`Tie: ${users.length} users (Diff: ${diff})`}
            fontSize={10}
            fill="#ffd700"
            align="center"
            verticalAlign="middle"
            offsetX={55}
            offsetY={5}
          />
        ) : (
          <Text
            x={0}
            y={-5}
            text={`Winner: ${users[0].username || users[0].name || 'User'} (Diff: ${diff})`}
            fontSize={10}
            fill={users[0].color || '#fff'}
            align="center"
            verticalAlign="middle"
            offsetX={60}
            offsetY={5}
          />
        )}
      </Group>
    );
  };

  // Early return if no predictions to display
  if (!predictions || predictions.length === 0) {
    return (
      <div className="playoff-score-grid-container">
        <div className="score-grid-legend">
          <h4>Score Grid Visualization</h4>
          <p style={{ color: '#ccc', margin: '10px 0' }}>No predictions available to display.</p>
        </div>
      </div>
    );
  }

  const homeTeamName = gameData?.homeTeamName || 'Home Team';
  const awayTeamName = gameData?.awayTeamName || 'Away Team';
  const spreadDisplay = gameData?.spread !== null && gameData?.spread !== undefined 
    ? (gameData.spread > 0 ? `+${gameData.spread.toFixed(1)}` : gameData.spread.toFixed(1))
    : 'N/A';
  const overUnderDisplay = gameData?.overUnder !== null && gameData?.overUnder !== undefined
    ? gameData.overUnder.toFixed(1)
    : 'N/A';

  return (
    <div className="playoff-score-grid-container">
      <div className="score-grid-legend">
        <h4>Score Grid Visualization</h4>
        <div className="legend-items">
          <div className="legend-item">
            <div className="legend-color" style={{ backgroundColor: '#0066ff' }}></div>
            <span>Spread Line ({spreadDisplay})</span>
          </div>
          <div className="legend-item">
            <div className="legend-color" style={{ backgroundColor: '#ff6600' }}></div>
            <span>O/U Line ({overUnderDisplay})</span>
          </div>
          <div className="legend-item">
            <div className="legend-color" style={{ backgroundColor: '#ffff00' }}></div>
            <span>Game Progress</span>
          </div>
          {predictions.map((pred, index) => (
            <div key={`legend-${index}`} className="legend-item">
              <div className="legend-color" style={{ backgroundColor: pred.color }}></div>
              <span>{pred.username}: {pred.home}-{pred.away}</span>
            </div>
          ))}
        </div>
      </div>
      
      <div className="score-grid-wrapper">
        <Stage
          ref={stageRef}
          width={STAGE_WIDTH}
          height={STAGE_HEIGHT}
        >
          <Layer>
            {/* Translate to make room for axis labels */}
            <Group x={LABEL_OFFSET} y={0}>
              {/* Grid cells */}
              {renderGridCells()}
              
              {/* Spread and O/U lines */}
              {renderSpreadLine()}
              {renderOverUnderLine()}
              
              {/* Game progress path */}
              {renderGameProgress()}
              
              {/* User predictions */}
              {renderUserPredictions()}
            </Group>
            
            {/* Axis labels */}
            <Group x={LABEL_OFFSET} y={0}>
              {renderAxisLabels()}
            </Group>
            
            {/* Axis titles - Home team name (rotated, offset left) */}
            <Text
              x={5}
              y={(GRID_SIZE_HOME * CELL_SIZE) / 2}
              text={homeTeamName}
              fontSize={12}
              fill="#ccc"
              align="center"
              verticalAlign="middle"
              offsetX={homeTeamName.length * 3}
              offsetY={6}
              rotation={-90}
            />
            {/* Axis titles - Away team name (shifted down below scores) */}
            <Text
              x={(GRID_SIZE_AWAY * CELL_SIZE) / 2 + LABEL_OFFSET}
              y={GRID_SIZE_HOME * CELL_SIZE + 40}
              text={awayTeamName}
              fontSize={12}
              fill="#ccc"
              align="center"
              verticalAlign="middle"
              offsetX={awayTeamName.length * 3}
              offsetY={6}
            />
            
            {/* Tooltip */}
            {tooltip && renderTooltip()}
          </Layer>
        </Stage>
      </div>
      
    </div>
  );
};

PlayoffScoreGrid.propTypes = {
  predictions: PropTypes.arrayOf(
    PropTypes.shape({
      username: PropTypes.string,
      name: PropTypes.string,
      home: PropTypes.number.isRequired,
      away: PropTypes.number.isRequired,
      color: PropTypes.string,
    })
  ),
  gameData: PropTypes.shape({
    spread: PropTypes.number,
    overUnder: PropTypes.number,
    homeTeamName: PropTypes.string,
    awayTeamName: PropTypes.string,
    currentScore: PropTypes.shape({
      home: PropTypes.number,
      away: PropTypes.number,
    }),
    scoreHistory: PropTypes.arrayOf(
      PropTypes.shape({
        home: PropTypes.number,
        away: PropTypes.number,
      })
    ),
  }),
  height: PropTypes.number,
};

// Memoize component to prevent unnecessary re-renders when props haven't changed
export default memo(PlayoffScoreGrid);
